import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Grid,
    Chip,
    IconButton,
    Tooltip,
} from "@mui/material";
import { Clear as ClearIcon, FilterList as FilterIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ru } from "date-fns/locale";
import { apiService } from "../services/api";
import { authStore } from "../stores";

interface StatsFiltersProps {
    startDate: Date | null;
    endDate: Date | null;
    clientId: number | null;
    onStartDateChange: (date: Date | null) => void;
    onEndDateChange: (date: Date | null) => void;
    onClientChange: (clientId: number | null) => void;
    onClearFilters: () => void;
}

interface Client {
    id: number;
    name: string;
}

const StatsFilters: React.FC<StatsFiltersProps> = ({
    startDate,
    endDate,
    clientId,
    onStartDateChange,
    onEndDateChange,
    onClientChange,
    onClearFilters,
}) => {
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(false);
    const userRole = authStore.user?.role || "supplier";
    const isClient = userRole === "client";

    useEffect(() => {
        const fetchClients = async () => {
            setLoading(true);
            try {
                const response = await apiService.getClients();
                // API возвращает { success: true, data: [...], total: ... }
                const clientsData = Array.isArray(response.data) 
                    ? response.data 
                    : (response.data?.data || []);
                setClients(clientsData);
            } catch (error) {
                console.error("Error fetching clients:", error);
                setClients([]);
            } finally {
                setLoading(false);
            }
        };

        fetchClients();
    }, []);

    const hasActiveFilters = startDate || endDate || clientId;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                        <FilterIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6" component="h3">
                            Фильтры
                        </Typography>
                        {hasActiveFilters && (
                            <Tooltip title="Очистить все фильтры">
                                <IconButton onClick={onClearFilters} size="small" sx={{ ml: 1 }}>
                                    <ClearIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="Дата начала"
                                value={startDate}
                                onChange={onStartDateChange}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="Дата окончания"
                                value={endDate}
                                onChange={onEndDateChange}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Клиент</InputLabel>
                                <Select
                                    value={
                                        clientId !== null && clientId !== undefined
                                            ? clients.find((c) => c.id === clientId)
                                                ? clientId
                                                : "all"
                                            : "all"
                                    }
                                    label="Клиент"
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        console.log("🔍 StatsFilters: Select onChange triggered, value:", newValue, "type:", typeof newValue);
                                        const finalClientId = newValue === "all" ? null : Number(newValue);
                                        console.log("🔍 StatsFilters: Calling onClientChange with:", finalClientId);
                                        onClientChange(finalClientId);
                                    }}
                                    disabled={loading || isClient}
                                >
                                    <MenuItem value="all">
                                        <em>Все клиенты</em>
                                    </MenuItem>
                                    {clients.map((client) => (
                                        <MenuItem key={client.id} value={client.id}>
                                            {client.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box display="flex" alignItems="center" height="100%">
                                {hasActiveFilters && (
                                    <Box>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                            Активные фильтры:
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                            {startDate && (
                                                <Chip
                                                    label={`С: ${startDate.toLocaleDateString("ru-RU")}`}
                                                    size="small"
                                                    onDelete={() => onStartDateChange(null)}
                                                />
                                            )}
                                            {endDate && (
                                                <Chip
                                                    label={`По: ${endDate.toLocaleDateString("ru-RU")}`}
                                                    size="small"
                                                    onDelete={() => onEndDateChange(null)}
                                                />
                                            )}
                                            {clientId && (
                                                <Chip
                                                    label={`Клиент: ${clients.find((c) => c.id === clientId)?.name || clientId}`}
                                                    size="small"
                                                    onDelete={isClient ? undefined : () => onClientChange(null)}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </LocalizationProvider>
    );
};

export default StatsFilters;
